import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, ShoppingBag } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { formatINR } from "../lib/api";
import QuickViewDialog from "./QuickViewDialog";

export default function ProductCard({ product }) {
  const { toggleWishlist, isWishlisted, addToCart } = useShop();
  const [quickOpen, setQuickOpen] = useState(false);
  const wished = isWishlisted(product.id);
  const outOfStock = product.stock <= 0;
  const discount = product.compare_price ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;

  return (
    <>
      <div data-testid={`product-card-${product.slug}`} className="card-product group relative">
        <Link to={`/product/${product.slug}`} className="block relative overflow-hidden aspect-[4/5] bg-[#0e0e0e]">
          <img
            src={product.images?.[0]}
            alt={product.name}
            className={`product-img w-full h-full object-cover ${outOfStock ? "opacity-50" : ""}`}
            loading="lazy"
          />
          {outOfStock ? (
            <span data-testid={`sold-out-badge-${product.slug}`} className="absolute top-3 left-3 bg-white/10 text-white/80 text-[11px] font-display tracking-widest px-2 py-1 uppercase">
              Sold Out
            </span>
          ) : discount > 0 && (
            <span className="absolute top-3 left-3 bg-[#D96A1B] text-white text-[11px] font-display tracking-widest px-2 py-1">
              -{discount}%
            </span>
          )}
          <button
            data-testid={`wishlist-btn-${product.slug}`}
            onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
            className={`absolute top-3 right-3 p-2 backdrop-blur bg-black/40 border border-white/20 transition-colors ${wished ? "text-[#D96A1B]" : "text-white"}`}
            aria-label="Wishlist"
          >
            <Heart size={16} fill={wished ? "#D96A1B" : "none"} />
          </button>

          <div className="absolute inset-x-0 bottom-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/90 to-transparent">
            <div className="flex gap-2">
              <button
                data-testid={`quick-view-btn-${product.slug}`}
                onClick={(e) => { e.preventDefault(); setQuickOpen(true); }}
                className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white text-[11px] font-display tracking-widest uppercase py-2.5 flex items-center justify-center gap-1.5"
              >
                <Eye size={14} /> Quick View
              </button>
              {outOfStock ? (
                <button
                  data-testid={`notify-me-btn-${product.slug}`}
                  className="flex-1 bg-white/10 border border-white/20 text-white/80 text-[11px] font-display tracking-widest uppercase py-2.5 flex items-center justify-center gap-1.5"
                >
                  Notify Me
                </button>
              ) : (
                <button
                  data-testid={`add-to-cart-btn-${product.slug}`}
                  onClick={(e) => { e.preventDefault(); addToCart(product, 1); }}
                  className="flex-1 bg-[#D96A1B] hover:bg-[#E87A2A] text-white text-[11px] font-display tracking-widest uppercase py-2.5 flex items-center justify-center gap-1.5"
                >
                  <ShoppingBag size={14} /> Add to Cart
                </button>
              )}
            </div>
          </div>
        </Link>

        <div className="p-4">
          <div className="overline text-[10px] mb-1.5">{product.subcategory}</div>
          <Link to={`/product/${product.slug}`} className="block">
            <h3 className="font-display text-base uppercase tracking-tight text-white hover:text-[#D96A1B] transition-colors line-clamp-2">
              {product.name}
            </h3>
          </Link>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-lg text-white">{formatINR(product.price)}</span>
            {product.compare_price && (
              <span className="text-xs text-white/40 line-through">{formatINR(product.compare_price)}</span>
            )}
          </div>
        </div>
      </div>

      <QuickViewDialog open={quickOpen} onClose={() => setQuickOpen(false)} product={product} />
    </>
  );
}
